<template>
  <div class="space-y-1">
    <div class="flex items-center justify-between w-full">
      <div>
        <label class="block mb-0 text-sm">Book a Meeting</label>
        <label class="mb-0 text-xs font-normal">Allow investors to book meetings with my company from this deck</label>
      </div>
      <VSwitch data-testid="bam-toggle" :value="isBookMeetingActive" @input="$emit('toggle-bam-enabled', $event)" />
    </div>

    <EditorDeckBookMeetingAlert
      v-if="isBookMeetingActive"
      class="py-2"
      :request-settings="requestSettings"
      :should-display-b-a-m-toast="shouldDisplayBAMToast"
      data-testid="bam-alert"
      @activate-book-a-meeting="$emit('activate-book-a-meeting', $event)"
      @sync-calendar="$emit('sync-calendar')"
      @set-up-calendar="$emit('set-up-calendar')"
    />

    <div v-if="shouldDisplayDropdown" class="w-96">
      <VDropdown
        v-model="currentUserOption"
        :options="users as unknown as Option[]"
        size="sm"
        data-testid="bam-dropdown"
        @update:model-value="handleDropdownInput"
      >
        <template #current-single="{ item }">{{ item.name ?? 'Select a user' }}</template>
        <template #option="{ item, selected }">
          <VDropdownItem
            :item="(item as unknown as ScheduleSetting).user.name"
            :selected="selected"
            :class="{ 'text-gray-900 font-semibold': selected }"
            :data-testid="`dropdown-item-${item.id}`"
          />
        </template>
      </VDropdown>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { toRefs, computed, watch } from 'vue'

import type { Option } from '@/components/VDropdown.vue'
import type { ScheduleSetting } from '~/entities/meeting'
import type { BookMeetingStatus, BookMeetingNoneAvailable, BookMeetingIncomplete } from '@/composables/useBookMeeting'
import { isBAMNotSetup, isBAMSetupIncomplete, isBAMUnavailable } from '~/composables/useBookMeeting'

const props = defineProps({
  requestSettings: {
    type: Object as PropType<DeepReadonly<BookMeetingStatus>>,
    required: true,
  },
  deckRequestSettings: {
    type: Object as PropType<DeepReadonly<ScheduleSetting> | null>,
    default: null,
  },
  isBookMeetingActive: {
    type: Boolean,
    default: false,
  },
  shouldDisplayBAMToast: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['toggle-bam-enabled', 'select-admin', 'activate-book-a-meeting', 'sync-calendar', 'set-up-calendar'])
const { requestSettings, isBookMeetingActive } = toRefs(props)
const { currentUser, users } = useBookMeetingDropdown(requestSettings)
const shouldDisplayDropdown = computed(
  () =>
    isBookMeetingActive.value &&
    !isBAMNotSetup(requestSettings.value as BookMeetingStatus) &&
    !isBAMUnavailable(requestSettings.value as BookMeetingStatus)
)

const handleDropdownInput = (value: ScheduleSetting) => {
  currentUserOption.value = { id: value.id, name: value.user.name }
  emit('select-admin', value)
}

const currentUserOption: Ref<Option | null> = ref(null)

watch(
  () => props.deckRequestSettings,
  newValue => {
    if (newValue) {
      currentUser.value = newValue as ScheduleSetting
      currentUserOption.value = { id: newValue.id, name: newValue.user.name }
    }
  },
  {
    immediate: true,
  }
)

watch(isBookMeetingActive, active => {
  if (!requestSettings.value || isBAMNotSetup(requestSettings.value as BookMeetingStatus)) return

  const currentSettings = isBAMSetupIncomplete(requestSettings.value as BookMeetingStatus)
    ? (requestSettings.value as BookMeetingIncomplete).users[0]
    : (requestSettings.value as BookMeetingNoneAvailable).currentUser

  currentUserOption.value = active ? { id: currentUser.value.id, name: currentUser.value.user.name } : null
  emit('select-admin', active ? currentUser.value ?? currentSettings : null)
})
</script>
